import { useSyncExternalStore } from 'react';

// Mantine portals its modals, drawers and Spotlight to <body>, outside the React tree, so the only
// reliable signal that one is open is the DOM itself. The root node stays mounted while closed; the
// inner panel exists only while the overlay is showing, unless keepMounted hides it in place.
const OVERLAY_SELECTOR = '.mantine-Modal-inner, .mantine-Drawer-inner, .mantine-Spotlight-inner';

function isShown(element: Element): boolean {
  for (let node: Element | null = element; node && node !== document.body; node = node.parentElement) {
    if (node instanceof HTMLElement && node.hidden) return false;
    const style = getComputedStyle(node);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
  }
  return true;
}

function isOverlayOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return Array.from(document.querySelectorAll(OVERLAY_SELECTOR)).some(isShown);
}

function subscribe(onChange: () => void): () => void {
  if (typeof MutationObserver === 'undefined') return () => {};
  const observer = new MutationObserver(onChange);
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['style', 'class', 'hidden'],
  });
  return () => observer.disconnect();
}

/** Whether a Mantine modal, drawer or Spotlight is currently on screen, so bottom-anchored chrome can stay out of its way. */
export function useOverlayOpen(): boolean {
  return useSyncExternalStore(subscribe, isOverlayOpen, () => false);
}
